import React, { useCallback, useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { ArrowCycle } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  ComplaintsCategoriesDocument,
  ComplaintsDataCollectionDocument,
  ComplaintsDataCollectionItemFragment,
  ComplaintsStatusEnum,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { useComplaintStatus } from 'modules/complaints/hooks/useComplaintStatus'
import {
  ComplaintStatus,
  STATUS,
  getStatusColor,
  isValidComplaintStatus,
  statusLabel,
} from 'modules/complaints/lib/utils/complaintStatus'
import { ChangeStatusModal } from 'modules/complaints/pages/ComplaintsPage/ChangeStatusModal'

const PAGINATION_CONFIG = {
  type: 'pages' as const,
  perPage: 20,
  defaultSortings: {
    field: 'createdAt' as const,
    order: 'desc' as const,
  },
} as const

interface ChangeStatusModalState {
  isOpen: boolean
  complaint: ComplaintsDataCollectionItemFragment | null
}

interface ComplaintsTableFilters {
  status?: ComplaintStatus[]
  category?: string[]
}

const DEFAULT_FILTERS: ComplaintsTableFilters = {
  status: [STATUS.Created, STATUS.Acknowledged, STATUS.Completed, STATUS.Discarded],
}

export const ComplaintsDataCollectionPage: React.FC = () => {
  const { navigation } = useNavigation()
  const client = useApolloClient()

  const [changeStatusModal, setChangeStatusModal] = useState<ChangeStatusModalState>({
    isOpen: false,
    complaint: null,
  })

  const [savedFilters, setSavedFilters] = useLocalStorage<ComplaintsTableFilters | undefined>(
    LOCAL_STORAGE_KEYS.complaintsTableFilters
  )

  const { getStatusOptions } = useComplaintStatus()

  const handleOpenChangeStatusModal = useCallback(
    (complaint: ComplaintsDataCollectionItemFragment) => {
      setChangeStatusModal({
        isOpen: true,
        complaint,
      })
    },
    []
  )

  const handleCloseChangeStatusModal = useCallback(() => {
    setChangeStatusModal({
      isOpen: false,
      complaint: null,
    })
  }, [])

  const getItemActions = useCallback(
    (complaint: ComplaintsDataCollectionItemFragment) => {
      const statusOptions = getStatusOptions(complaint)

      const actions = []

      if (statusOptions.length > 0) {
        actions.push({
          label: i18n.t('complaints.overview.change_status'),
          icon: ArrowCycle,
          onClick: () => handleOpenChangeStatusModal(complaint),
        })
      }

      return actions
    },
    [getStatusOptions, handleOpenChangeStatusModal]
  )

  const source = useGraphqlDataCollectionSource({
    document: ComplaintsDataCollectionDocument,
    mapData: (data) => data.complaints.complaintsConnection.nodes,
    mapPagination: (data) => ({
      total: data.complaints.complaintsConnection.totalCount ?? 0,
    }),
    currentFilters: savedFilters ?? DEFAULT_FILTERS,
    mapVariables: ({ search, sortings, filters }) => {
      const statuses =
        filters.status && filters.status.length > 0
          ? filters.status.filter((status): status is ComplaintsStatusEnum =>
              isValidComplaintStatus(status)
            )
          : undefined
      const categoryIds =
        filters.category && filters.category.length > 0
          ? { in: filters.category.map(Number).filter(Boolean) }
          : undefined

      setSavedFilters(filters)

      return {
        search,
        sort: sortings?.[0],
        statuses,
        categoryIds,
      }
    },
    defaultSortings: PAGINATION_CONFIG.defaultSortings,
    paginationType: PAGINATION_CONFIG.type,
    perPage: PAGINATION_CONFIG.perPage,
    search: {
      enabled: true,
    },
    itemUrl: (complaint: ComplaintsDataCollectionItemFragment) =>
      navigation.complaints.id.fullPath({ id: complaint.id }),
    itemActions: getItemActions,
    filters: {
      status: {
        label: i18n.t('complaints.overview.status'),
        type: 'in',
        options: {
          options: [
            { label: i18n.t('complaints.status.created'), value: STATUS.Created },
            { label: i18n.t('complaints.status.in_progress'), value: STATUS.Acknowledged },
            { label: i18n.t('complaints.status.processed'), value: STATUS.Completed },
            { label: i18n.t('complaints.status.discarded'), value: STATUS.Discarded },
            { label: i18n.t('complaints.status.archived'), value: STATUS.Archived },
          ],
        },
      },
      category: {
        label: i18n.t('complaints.overview.category'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: ComplaintsCategoriesDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.complaints.categoriesConnection.nodes.map((category) => ({
                label: category.name,
                value: category.id.toString(),
              })) || []
            )
          },
        },
      },
    },
  })

  const tableColumns = useMemo(
    () => [
      {
        label: i18n.t('complaints.overview.identifier'),
        render: (complaint: ComplaintsDataCollectionItemFragment) => {
          const identifier =
            complaint.identifier ||
            i18n.t('complaints.overview.complaint', { complaintId: complaint.id })
          return {
            type: 'text' as const,
            value: identifier,
          }
        },
      },
      {
        label: i18n.t('complaints.overview.status'),
        sorting: 'status',
        render: (complaint: ComplaintsDataCollectionItemFragment) => ({
          type: 'status' as const,
          value: {
            status: getStatusColor(complaint.status),
            label: statusLabel(complaint.status),
          },
        }),
      },
      {
        label: i18n.t('complaints.overview.submitted_by'),
        sorting: 'complainerName',
        render: (complaint: ComplaintsDataCollectionItemFragment) => ({
          type: 'person' as const,
          value: {
            firstName: complaint.complainerName || i18n.t('complaints.overview.unknown'),
            lastName: '',
          },
        }),
      },
      {
        label: i18n.t('complaints.overview.date_submitted'),
        sorting: 'createdAt',
        render: (complaint: ComplaintsDataCollectionItemFragment) =>
          convertDate(complaint.createdAt, 'backend_datetime', 'ui_abbreviated_month_year'),
      },
      {
        label: i18n.t('complaints.overview.category'),
        render: (complaint: ComplaintsDataCollectionItemFragment) => ({
          type: 'text' as const,
          value: complaint.category?.name || i18n.t('complaints.category.none'),
        }),
      },
    ],
    []
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns: tableColumns,
        },
      },
    ],
    [tableColumns]
  )

  return (
    <>
      <OneDataCollection source={source} visualizations={visualizations} />

      {changeStatusModal.complaint && (
        <ChangeStatusModal
          isOpen={changeStatusModal.isOpen}
          onClose={handleCloseChangeStatusModal}
          complaint={changeStatusModal.complaint}
        />
      )}
    </>
  )
}
